import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import {
  useCategoryTheme,
  resolveTheme,
} from "@/context/CategoryThemeContext";

export const categoryIcon = (name) =>
  Icons[name] || Icons.Sparkles;

export default function CategoryBar({
  categories = [],
  active,
}) {
  const navigate = useNavigate();

  const [isScrolled, setIsScrolled] = useState(false);
  const categoryScrollerRef = useRef(null);
  const dragRef = useRef({ active: false, startX: 0, startLeft: 0, moved: false });
  const pauseAutoScrollUntilRef = useRef(0);

  const { applyTheme } = useCategoryTheme();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 80);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const el = categoryScrollerRef.current;
    if (!el) return;

    const timer = window.setInterval(() => {
      if (document.hidden || Date.now() < pauseAutoScrollUntilRef.current) return;

      const items = Array.from(
        el.querySelectorAll('[data-category-scroll-item="true"]')
      );
      if (items.length < 2) return;

      const containerRect = el.getBoundingClientRect();
      const visibleItems = items.filter((item) => {
        const rect = item.getBoundingClientRect();
        return rect.left >= containerRect.left + 8 &&
          rect.left < containerRect.right - 8;
      });

      const current = visibleItems[0];
      const currentIndex = current ? items.indexOf(current) : -1;
      const next = items[currentIndex + 1];

      if (!next) {
        el.scrollTo({ left: 0, behavior: "smooth" });
        return;
      }

      const nextRect = next.getBoundingClientRect();
      const targetLeft = el.scrollLeft + nextRect.left - containerRect.left - 8;
      el.scrollTo({ left: targetLeft, behavior: "smooth" });
    }, 3000);

    return () => window.clearInterval(timer);
  }, [categories.length, isScrolled]);

  if (!categories?.length) return null;

  const go = (category) => {
    applyTheme(category.theme, category.slug);

    navigate(
      category.is_all
        ? "/search"
        : `/category/${category.slug}`
    );
  };

  return (
    <section
      data-testid="category-bar"
      className={`
        z-40
        w-full
        bg-white/95
        backdrop-blur-md
        border-b
        border-slate-100
        shadow-sm
        transition-all
        duration-300
        ${isScrolled ? "sticky top-[112px] sm:top-20" : "relative"}
      `}
    >
      <div
        className={`
          mx-auto
          w-full
          transition-all
          duration-300
          ${isScrolled
            ? "max-w-7xl px-2 sm:px-6"
            : "max-w-7xl px-3 sm:px-6"
          }
        `}
      >
        <div
          ref={categoryScrollerRef}
          onPointerDown={(e) => {
            pauseAutoScrollUntilRef.current = Date.now() + 7000;
            if (e.pointerType !== "mouse" || e.button !== 0) return;
            dragRef.current = {
              active: true,
              startX: e.clientX,
              startLeft: e.currentTarget.scrollLeft,
              moved: false,
            };
          }}
          onPointerMove={(e) => {
            if (!dragRef.current.active) return;
            const dx = e.clientX - dragRef.current.startX;
            if (Math.abs(dx) > 4) dragRef.current.moved = true;
            if (dragRef.current.moved) {
              e.currentTarget.scrollLeft = dragRef.current.startLeft - dx;
            }
          }}
          onPointerUp={() => {
            dragRef.current.active = false;
            pauseAutoScrollUntilRef.current = Date.now() + 7000;
          }}
          onPointerLeave={() => {
            dragRef.current.active = false;
          }}
          onPointerCancel={() => {
            dragRef.current.active = false;
            dragRef.current.moved = false;
          }}
          onClickCapture={(e) => {
            if (dragRef.current.moved) {
              e.preventDefault();
              e.stopPropagation();
              dragRef.current.moved = false;
            }
          }}
          onWheel={() => {
            pauseAutoScrollUntilRef.current = Date.now() + 7000;
          }}
          onTouchStart={() => {
            pauseAutoScrollUntilRef.current = Date.now() + 7000;
          }}
          className={`
            flex
            items-center
            overflow-x-auto
            no-scrollbar
            scroll-smooth
            ${
              isScrolled
                ? "gap-1.5 sm:gap-2 py-2"
                : "gap-2 sm:gap-3 py-2.5 sm:py-3"
            }
          `}
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          {categories.map((category) => {
            const Icon = categoryIcon(category.icon);

            const isActive =
              active === category.slug ||
              (category.is_all && active === "all");

            const theme = resolveTheme(
              category.theme,
              category.slug
            );

            const shortCategoryLabels = {
      venues: "Venues",
      "venues-banquets": "Venues",
      photography: "Photography",
      "photography-video": "Photography",
      catering: "Catering",
      "catering-food": "Catering",
      decoration: "Decoration",
      "decoration-florals": "Decoration",
      entertainment: "Entertainment",
      beauty: "Beauty",
      "beauty-makeup": "Beauty",
      fashion: "Fashion",
      "fashion-jewellery": "Fashion",
      event: "Event",
      "event-planning": "Event",
      sound: "Sound",
      "sound-lighting": "Sound",
      transportation: "Transport",
      transport: "Transport",
      rentals: "Rentals",
    };

    const normalizedName = (category.name || "").toLowerCase();

    const displayName = category.is_all
      ? "All"
      : shortCategoryLabels[category.slug] ||
        (normalizedName.includes("venue") ? "Venues" :
        normalizedName.includes("photograph") ? "Photography" :
        normalizedName.includes("cater") ? "Catering" :
        normalizedName.includes("decor") || normalizedName.includes("floral") ? "Decoration" :
        normalizedName.includes("entertain") ? "Entertainment" :
        normalizedName.includes("beauty") || normalizedName.includes("makeup") ? "Beauty" :
        normalizedName.includes("fashion") || normalizedName.includes("jewell") ? "Fashion" :
        normalizedName.includes("event") ? "Event" :
        normalizedName.includes("sound") || normalizedName.includes("lighting") ? "Sound" :
        normalizedName.includes("transport") ? "Transport" :
        normalizedName.includes("rental") ? "Rentals" :
        category.name || "Explore");

            /*
             * =====================================================
             * SCROLLED STATE
             * Compact text-only category navigation.
             * No icons.
             * Active category gets purple -> pink gradient.
             * =====================================================
             */

            if (isScrolled) {
              return (
                <motion.button
                  key={category.slug}
                  type="button"
                  data-testid={`category-chip-${category.slug}`}
                  data-category-scroll-item={!category.is_all ? "true" : undefined}
                  data-active={isActive}
                  onClick={() => {
                    if (dragRef.current.moved) {
                      dragRef.current.moved = false;
                      return;
                    }
                    go(category);
                  }}
                  whileTap={{ scale: 0.96 }}
                  className={`
                    flex-shrink-0
                    ${category.is_all ? "sticky left-0 z-20 bg-white/95 shadow-sm" : ""}
                    flex
                    items-center
                    justify-center
                    whitespace-nowrap
                    rounded-full
                    px-4
                    py-2
                    text-[12px]
                    sm:text-[13px]
                    font-semibold
                    transition-all
                    duration-200
                    ${
                      isActive
                        ? "bg-gradient-to-r from-purple-600 via-purple-600 to-pink-500 text-white shadow-md shadow-purple-200"
                        : "bg-transparent text-slate-700 hover:bg-purple-50 hover:text-purple-600"
                    }
                  `}
                >
                  {displayName}
                </motion.button>
              );
            }

            /*
             * =====================================================
             * TOP / HOMEPAGE STATE
             * Icon + category name.
             * This state is identical across desktop/tablet/mobile.
             * =====================================================
             */

            return (
              <motion.button
                key={category.slug}
                type="button"
                data-testid={`category-chip-${category.slug}`}
                data-category-scroll-item={!category.is_all ? "true" : undefined}
                data-active={isActive}
                onClick={() => {
                    if (dragRef.current.moved) {
                      dragRef.current.moved = false;
                      return;
                    }
                    go(category);
                  }}
                whileTap={{ scale: 0.96 }}
                className={`
                  group
                  ${category.is_all ? "sticky left-0 z-20 bg-white/95 rounded-xl" : ""}
                  flex-shrink-0
                  flex
                  flex-col
                  items-center
                  justify-center
                  gap-1
                  min-w-[70px]
                  sm:min-w-[76px]
                  rounded-xl
                  px-2
                  py-1
                  transition-all
                  duration-200
                `}
              >
                <span
                  className={`
                    flex
                    items-center
                    justify-center
                    w-10
                    h-10
                    sm:w-11
                    sm:h-11
                    rounded-xl
                    transition-all
                    duration-200
                    ${
                      isActive
                        ? "bg-gradient-to-br from-purple-600 via-purple-600 to-pink-500 text-white shadow-md shadow-purple-200"
                        : "bg-slate-50 text-slate-600 group-hover:bg-purple-50 group-hover:text-purple-600"
                    }
                  `}
                  style={
                    !isActive
                      ? {
                          color:
                            theme?.accent || undefined,
                        }
                      : undefined
                  }
                >
                  <Icon
                    className="
                      w-[19px]
                      h-[19px]
                      sm:w-5
                      sm:h-5
                    "
                    strokeWidth={2}
                  />
                </span>

                <span
                  className={`
                    max-w-[100px]
                    sm:max-w-[110px]
                    truncate
                    text-[10px]
                    sm:text-[11px]
                    font-semibold
                    leading-none
                    whitespace-nowrap
                    text-center
                    transition-all
                    duration-200
                    ${
                      isActive
                        ? "text-purple-700"
                        : "text-slate-700"
                    }
                  `}
                >
                  {displayName}
                </span>
              </motion.button>
            );
          })}

          {/* MORE */}
          <motion.button
            type="button"
            data-testid="category-chip-more"
            onClick={() => navigate("/search")}
            whileTap={{ scale: 0.96 }}
            className={`
              flex-shrink-0
              flex
              items-center
              justify-center
              whitespace-nowrap
              rounded-full
              font-semibold
              transition-all
              duration-200
              ${
                isScrolled
                  ? "px-4 py-2 text-[12px] sm:text-[13px] text-purple-600 hover:bg-purple-50"
                  : "flex-col gap-1 min-w-[70px] sm:min-w-[76px] px-2 py-1 text-[10px] sm:text-[11px] text-slate-700"
              }
            `}
          >
            {!isScrolled && (
              <span
                className="
                  flex
                  items-center
                  justify-center
                  w-10
                  h-10
                  sm:w-11
                  sm:h-11
                  rounded-xl
                  bg-slate-50
                  text-purple-600
                "
              >
                <Icons.MoreHorizontal
                  className="w-5 h-5"
                  strokeWidth={2}
                />
              </span>
            )}

            <span>
              More
            </span>
          </motion.button>
        </div>
      </div>
    </section>
  );
}
